import React from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Grid,
    Divider,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EnhancedTextField from '../../../../../components/EnhancedTextField';
import EnhancedSelect from '../../../../../components/EnhancedSelect';
import { AttributeInput } from './AttributeInput';
import { RunRule } from '../../../../inventory/shared/validation';
import type {
    ProductAttribute,
    ProductCategory,
    ProductVariant,
    VariesBy,
    VariesByOption,
} from "../types";

// region TypeScript Interfaces
export interface ProductVariantAddProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onAdd: (data: Record<string, any>) => void;
    variesBy?: VariesBy;
    variations?: ProductVariant[];
    availableAttributes?: ProductAttribute[];
    availableCategories?: ProductCategory[];
}
// endregion

const EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG = [
    'image',
    'image1',
    'image2',
    'image3',
    'image4',
    'image5',
    'image6',
    'image7',
    'image8',
    'image9',
    'image10',
    'image11',
    'image12',
    'image13',
    'image14',
    'image15',
];

const StyledDialog = styled(Dialog)({
    "& .MuiPaper-rounded": {
        borderRadius: "12px",
        padding: "16px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column"
    },
    "& .MuiDialogTitle-root": {
        padding: "0 0 16px 0",
        flexShrink: 0
    },
    "& .MuiDialogContent-root": {
        padding: 0,
        overflowY: "auto",
        overflowX: "hidden",
        flexGrow: 1,
        // Hide scrollbar on Windows while keeping scroll functionality
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0,0,0,0.3)",
        }
    },
    "& .MuiDialogActions-root": {
        flexShrink: 0
    }
});

const ProductVariantAdd = ({ open, setOpen, onAdd, variesBy = { options: [], fields: [] }, variations = [], availableAttributes = [], availableCategories = [] }: ProductVariantAddProps) => {
    const [sku, setSku] = React.useState('');
    const [variesByValues, setVariesByValues] = React.useState<Record<string, any>>({});
    const [variantFieldValues, setVariantFieldValues] = React.useState<Record<string, any>>({});
    const [errors, setErrors] = React.useState<Record<string, string>>({});


    const getAttributeDetails = (attributeName: string): ProductAttribute => {
        return (availableAttributes.find(attr => attr.Name === attributeName) || {}) as ProductAttribute;
    };

    const renderAttributeInput = (option: VariesByOption, value: any, setValue: React.Dispatch<React.SetStateAction<Record<string, any>>>, error: string | undefined, isRequired = true) => {
        // Special handling for category field
        if (option.value === 'category') {
            const selectedCategory = availableCategories.find(cat => cat.Name === value);
            return (
                <EnhancedSelect
                    label={option.label}
                    value={selectedCategory ? {
                        value: selectedCategory.Name,
                        label: selectedCategory.Name,
                        path: selectedCategory.Path
                    } : null}
                    onChange={(selected: any) => {
                        setValue(prev => ({ ...prev, [option.value]: selected?.value || '' }));
                        setErrors(prev => ({ ...prev, [option.value]: '' }));
                    }}
                    options={availableCategories.map(cat => ({
                        value: cat.Name,
                        label: cat.Name,
                        path: cat.Path,
                    }))}
                    {...(error && { error: error, helperText: error })}
                    required={isRequired}
                    formatOptionLabel={(opt: any) => (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{opt.label}</span>
                            {opt.path && <span style={{ fontSize: 'small', color: 'rgba(0, 0, 0, 0.6)' }}>{opt.path}</span>}
                        </div>
                    )}
                    itemHeight={80}
                />
            );
        }
        
        const attributeDetails = getAttributeDetails(option.value);
        const attribute: ProductAttribute = {
            Name: option.value,
            FriendlyName: attributeDetails.FriendlyName || option.label as string || option.value,
            Type: (attributeDetails.Type as string) || option.type || 'text',
            Options: attributeDetails.Options,
            Editor: attributeDetails.Editor,
            ValidationRules: attributeDetails.ValidationRules
        };
        
        const handleChange = (attributeName: string, newValue: any) => {
            setValue(prev => ({ ...prev, [attributeName]: newValue }));
            setErrors(prev => ({ ...prev, [attributeName]: '' }));
        };
        
        return (
            <AttributeInput
                attribute={attribute}
                value={value}
                onChange={handleChange}
                error={error}
                label={option.label}
                disabled={false}
                required={isRequired}
            />
        );
    };

    const handleClose = () => {
        setSku('');
        setVariesByValues({});
        setVariantFieldValues({});
        setErrors({});
        setOpen(false);
    };

    const validateAttributeWithRules = (attribute: ProductAttribute, value: any): string | null => {
        if (!attribute.ValidationRules || attribute.ValidationRules.length === 0) return null;

        const tempProduct = {
            ...variesByValues,
            ...variantFieldValues,
            [attribute.Name]: value
        };
        
        let errorMessage: string | null = null;
        
        // Run each validation rule
        attribute.ValidationRules.forEach((rule: any) => {
            const runRule = RunRule(tempProduct, {
                ...rule,
                Field: attribute.Name,
                DescriptiveField: attribute.FriendlyName || attribute.Name || (attribute as any).label
            });
            
            if (runRule.IsValid === false && !errorMessage) {
                errorMessage = runRule.Message;
            }
        });
        
        return errorMessage;
    };

    const handleAdd = () => {
        const newErrors: Record<string, string> = {};

        if (!sku) {
            newErrors.sku = 'SKU is required';
        }

        // Validate all variesBy fields are filled (apply same filters as UI)
        variesBy.options
            .filter((option) => !EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG.includes(option.value))
            .filter((option) => option.value === 'category' || availableAttributes.some(attr => attr.Name === option.value))
            .forEach(option => {
                const value = variesByValues[option.value];
                // Check for undefined, null, or empty string (but allow 0 and "0")
                if (value === undefined || value === null || value === '') {
                    newErrors[option.value] = `${option.label} is required`;
                }
            });

        // Validate variant fields that have validation rules
        variesBy.fields.forEach(field => {
            const attributeDetails = getAttributeDetails(field.value);
            const value = variantFieldValues[field.value];
            if (attributeDetails.ValidationRules && attributeDetails.ValidationRules.length > 0) {
                const attribute: ProductAttribute = {
                    Name: field.value,
                    FriendlyName: field.label as string,
                    ValidationRules: attributeDetails.ValidationRules
                };
                const error = validateAttributeWithRules(attribute, value);
                if (error) {
                    newErrors[field.value] = error;
                }
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        
        const variantData = {
            sku,
            ...variesByValues,
            ...variantFieldValues
        };
        
        onAdd(variantData);
        handleClose();
    };


    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Add Variation</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginTop: '8px', marginBottom: '8px' }}>
                    {/* VariesBy Fields Section */}
                    <Grid item size={12}>
                        <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                            Varies By
                        </Typography>
                    </Grid>
                    
                    <Grid item size={6}>
                        <EnhancedTextField
                            label="SKU"
                            value={sku}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setSku(e.target.value);
                                setErrors(prev => ({ ...prev, sku: '' }));
                            }}
                            error={errors?.sku}
                            helperText={errors?.sku || ''}
                            fullWidth
                            required={true}
                        />
                    </Grid>
                    
                    {variesBy.options
                        .filter((option) => !EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG.includes(option.value))
                        .filter((option) => option.value === 'category' || availableAttributes.some(attr => attr.Name === option.value))
                        .map((option) => {
                            const optionAttribute = getAttributeDetails(option.value);
                            const isTextarea = optionAttribute.Editor === 'textarea' || optionAttribute.Editor === 'html';
                            
                            return (
                                <Grid item size={isTextarea ? 12 : 6} key={option.value}>
                                    {renderAttributeInput(
                                        option,
                                        variesByValues[option.value],
                                        setVariesByValues,
                                        errors?.[option.value],
                                        true // VariesBy fields are required
                                    )}
                                </Grid>
                            );
                        })}
                    
                    {/* Separator */}
                    {variesBy.fields
                        .filter((field) => !EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG.includes(field.value))
                        .filter((field) => field.value === 'category' || availableAttributes.some(attr => attr.Name === field.value))
                        .length > 0 && (
                        <Grid item size={12}>
                            <Divider style={{ margin: '16px 0' }} />
                        </Grid>
                    )}
                    
                    {/* Variant Fields with existing data */}
                    {variesBy.fields
                        .filter((field) => !EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG.includes(field.value))
                        .filter((field) => field.value === 'category' || availableAttributes.some(attr => attr.Name === field.value))
                        .length > 0 && (
                        <>
                            <Grid item size={12}>
                                <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                                    Variant Fields
                                </Typography>
                            </Grid>
                            {variesBy.fields
                                .filter((field) => !EXCLUDED_ATTRIBUTES_FROM_ADD_DIALOG.includes(field.value))
                                .filter((field) => field.value === 'category' || availableAttributes.some(attr => attr.Name === field.value))
                                .map((field) => {
                                    const fieldAttribute = getAttributeDetails(field.value);
                                    const isTextarea = fieldAttribute.Editor === 'textarea' || fieldAttribute.Editor === 'html';
                                    
                                    return (
                                        <Grid item size={isTextarea ? 12 : 6} key={field.value}>
                                            {renderAttributeInput(
                                                field,
                                                variantFieldValues[field.value],
                                                setVariantFieldValues,
                                                errors?.[field.value], // Pass actual errors for variant fields
                                                false // Variant fields are optional
                                            )}
                                        </Grid>
                                    );
                                })}
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '16px 0 0 0' }}>
                <Button onClick={handleClose} variant="text">
                    Cancel
                </Button>
                <Button onClick={handleAdd} color="primary" variant="text">
                    Add
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default ProductVariantAdd;
